import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2 } from 'lucide-react';
import { ImageUploader } from './ImageUploader';
import { Field } from './Field';
import { Switch } from '../ui/switch';
import { AllergenPicker } from './AllergenPicker';

export const SortableRow = ({ id, it, idx, t, updateItem, deleteItem, uploadPhoto, resolveImg, translate, allergens }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  const translateField = async (sourceField, targetField) => {
    try {
      const result = await translate(it[sourceField]?.it || '');
      updateItem(idx, targetField, result);
    } catch (e) {
      alert('Traduzione fallita: ' + e.message);
    }
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`border-[1.5px] rounded-2xl p-4 md:p-5 bg-white ${isDragging ? 'border-black shadow-[6px_6px_0_0_#0A0A0A]' : 'border-black/15'}`}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="mt-2 p-2 -ml-1 rounded-lg hover:bg-black/5 cursor-grab active:cursor-grabbing touch-none text-black/50 hover:text-black transition"
          aria-label="Trascina per riordinare"
          title="Trascina per riordinare"
        >
          <GripVertical size={18} />
        </button>
        <div className="flex-1 flex flex-col md:flex-row gap-4">
          <ImageUploader
            value={it.image}
            onChange={(v) => updateItem(idx, 'image', v)}
            onUpload={(file) => uploadPhoto(file, (url) => updateItem(idx, 'image', url))}
            resolveImg={resolveImg}
            t={t}
          />
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={`${t('name')} IT`} value={it.name?.it || ''} onChange={(v) => updateItem(idx, 'name_it', v)} />
            <Field
              label={`${t('name')} EN`}
              value={it.name?.en || ''}
              onChange={(v) => updateItem(idx, 'name_en', v)}
              onTranslate={translate ? () => translateField('name', 'name_en') : undefined}
              translateDisabled={!it.name?.it}
            />
            <Field label={`${t('description')} IT`} value={it.desc?.it || ''} onChange={(v) => updateItem(idx, 'desc_it', v)} textarea />
            <Field
              label={`${t('description')} EN`}
              value={it.desc?.en || ''}
              onChange={(v) => updateItem(idx, 'desc_en', v)}
              textarea
              onTranslate={translate ? () => translateField('desc', 'desc_en') : undefined}
              translateDisabled={!it.desc?.it}
            />
            <Field label={`${t('price')} (\u20ac)`} value={it.price || ''} onChange={(v) => updateItem(idx, 'price', v)} />
            <AllergenPicker label={t('allergens')} value={it.allergens} allergens={allergens} onChange={(v) => updateItem(idx, 'allergens', v)} testId={`item-allergens-${id}`} />
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <label className="inline-flex items-center gap-2 text-sm font-bold text-black cursor-pointer">
          <Switch
            checked={it.visible !== false}
            onCheckedChange={(v) => updateItem(idx, 'visible', v)}
            data-testid={`item-visible-toggle-${id}`}
          />
          {it.visible !== false ? 'Attivo' : 'Disattivato'}
        </label>
        <button data-testid={`item-delete-${id}`} onClick={() => deleteItem(idx)} className="inline-flex items-center gap-1 text-red-700 hover:text-red-900 font-bold text-sm">
          <Trash2 size={14} /> {t('delete')}
        </button>
      </div>
    </li>
  );
};

export default SortableRow;
